import { IAdminNotificationsDataLayer } from "@whatsllyorg/crm-components/TuvisSettings";
import { putJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "../../../../services/utils";
import { inject, injectable } from "inversify";
import type { IOrganizationStore } from "../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../services/IOrganizationStore";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { OrgConfigsStore } from "../../../../services/OrgConfigsStore";

@injectable()
export class AdminNotificationsPlatformDataLayer implements IAdminNotificationsDataLayer {
  private logger: FrontLogger;
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) loggerServer: FrontLogger,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore
  ) {
    this.logger = loggerServer.getLogger("admin-notifications-platform-dataLayer");
  }

  get isReadonly() {
    return !hasPermission(this.userStore.user, UserPermissions["notifications.update"]);
  }

  async init(): Promise<{ flags: string[]; emails: string[]; allowSyncGroups: boolean }> {
    const orgId = await this.orgStore.orgId;
    const userId = this.userStore.user?.userId;

    if (!orgId || !userId) {
      throw new Error("missing user id or org id");
    }
    const configs = await this.orgConfigsStore.getObservableConfigs(["allow_to_sync_group", "enabledNotifications", "notificationRecipients"]);

    return {
      allowSyncGroups: Boolean(configs?.allow_to_sync_group),
      flags: configs?.enabledNotifications || [],
      emails: configs?.notificationRecipients || [],
    };
  }

  async save(data: { flags: string[] | undefined; emails: string[] }): Promise<{ success: boolean }> {
    try {
      const orgId = await this.orgStore.orgId;
      const userId = this.userStore.user?.userId;

      if (!orgId || !userId) {
        throw new Error("missing user id or org id");
      }

      const res = await putJSON({
        urlSuffix: `/integrations/organizations/configs/notifications`,
        etoken: this.userStore.etoken ?? "",
        pod: import.meta.env.VITE_API_DEFAULT_POD,
        body: JSON.stringify({
          orgId,
          userId,
          emails: data.emails.filter((email) => email !== ""),
          flags: data.flags,
        }),
      }).then((r) => parseOrReturnError<{ success: boolean }>(r, this.logger));

      return { success: res?.success ?? false };
    } catch (err) {
      this.logger.error({ err }, "failed to save admin notifications");
      return { success: false };
    }
  }
}
